import { useFetchGet } from "Services/api";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { isNotEmptyArray } from "Services/functions";
import Margin from "Components/Stats/Margin";
import { getOffersByCategoryData } from "Components/Stats/Data/Offres/Categories";
import NivoPie from "Components/Stats/Charts/NivoPie";

/**
 * Composant 'OffersByCategory'.
 *
 * Ce composant affiche un graphique des offres par catégorie en utilisant les données
 * récupérées à partir de l'API. Les données formatées pour le graphique sont stockées
 * dans l'état local du composant via le hook useState.
 *
 * - {@link getOffersByCategoryData} : Fonction qui formate les données pour le graphique des offres par catégorie.
 *
 * - {@link NivoPie} : Composant générique de graphique en camembert implémenté avec la librairie Nivo.
 *
 *
 * @param {Object} props - Les propriétés du composant.
 * @param {Object} props.auth - Les informations d'authentification de l'utilisateur (depuis Redux).
 * @param {String} props.auth.token - Le token de l'utilisateur connecté.
 * @param {Object} props.secondaryTables - Les données des tables secondaires (depuis Redux)
 * @param {Array<{label: String, id: Number}>|undefined} props.secondaryTables.offerCategoriesData - Liste des catégories d'offres.
 * @param {Object} props.states - Les états des offres (depuis Redux).
 * @param {Array<{statusLabel: String, id: Number}>} props.states.statesData - Liste des états
 * @param {String} props.universityName - Le nom de l'université actuelle.
 * @returns {JSX.Element} Le graphique des offres par catégorie.
 *
 * @example
 * // Utilisation du composant 'OffersByCategory' dans un autre composant.
 * import OffersByCategory from './OffersByCategory';
 * // ...
 * <OffersByCategory
 *   universityName="Nom de l'université"
 * />
 */
const OffersByCategory = (props) => {
  /* Données pour graphique des offres par catégorie */

  const [offersByCategory, setOffersByCategory] = useState([]);

  const offersCountCategory = useFetchGet(
    "/offers/stats/count/category",
    props.auth.token,
    {
      // Filtre état: publié
      "state[]": props.states.statesData.find(
        (state) => state.statusLabel === "Publié"
      ).id,
    }
  );

  useEffect(() => {
    if (
      isNotEmptyArray(props.secondaryTables.offerCategoriesData) &&
      offersCountCategory.loaded
    ) {
      setOffersByCategory(
        getOffersByCategoryData(
          offersCountCategory?.data || [],
          props.secondaryTables.offerCategoriesData || []
        )
      );
    }
  }, [offersCountCategory.loaded]);

  return (
    <>
      <NivoPie
        key={offersByCategory.data}
        data={offersByCategory}
        title={
          "Nombre d'offres par catégorie en ligne à ce jour dans l'université " +
          props.universityName
        }
        labelName="Catégorie"
        legendWidth={115}
        displayPercentage
      />
      <Margin position="bottom" size="s" />
    </>
  );
};

OffersByCategory.propTypes = {
  auth: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
  states: PropTypes.object.isRequired,
  universityName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.states,
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

export default connect(mapStateToProps)(OffersByCategory);
